import {
  Box,
  Button,
  Grid,
  HStack,
  IconButton,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { MouseEvent, ReactNode } from "react";
import { FiTrash2 } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import type { AlgoliaAdCampaignObject } from "common/interfaces/Algolia";
import { formatDraftBudget, formatDraftLastEdited } from "../utils";

/** Same three-band layout as Figma: info | metrics | actions. */
export const DRAFT_ROW_GRID_TEMPLATE = "minmax(0, 1fr) 257px auto";
/** Align with active/past table horizontal scroll breakpoint. */
export const DRAFT_TABLE_MIN_WIDTH = "880px";
const DRAFT_METRICS_GAP = 12;

function DraftMetricCell({
  label,
  children,
  muted = false,
}: {
  label: string;
  children: ReactNode;
  muted?: boolean;
}) {
  return (
    <VStack align="flex-start" spacing={1.5} minW={0}>
      <Text
        fontSize="xs"
        color="gray.500"
        textTransform="uppercase"
        fontWeight="700"
        letterSpacing="0.6px"
        lineHeight="16px"
      >
        {label}
      </Text>
      <Text
        fontSize="md"
        color={muted ? "rgba(26, 32, 44, 0.62)" : "gray.800"}
        lineHeight="1"
        noOfLines={1}
      >
        {children}
      </Text>
    </VStack>
  );
}

function DraftEditCampaignButton({
  campaignId,
  isDisabled,
}: {
  campaignId: string;
  isDisabled: boolean;
}) {
  return (
    <Button
      as={Link}
      to={`/campaigns/ads/${campaignId}`}
      size="sm"
      variant="outline"
      bg="white"
      borderColor="gray.300"
      borderRadius="8px"
      fontWeight="500"
      fontSize="sm"
      color="gray.800"
      boxShadow="sm"
      px={4}
      py={2.5}
      h="auto"
      isDisabled={isDisabled}
      _hover={{ textDecoration: "none" }}
    >
      Edit campaign
    </Button>
  );
}

function DraftCampaignRowActions({
  campaignId,
  isDeleting,
  onDelete,
}: {
  campaignId: string;
  isDeleting: boolean;
  onDelete: () => void;
}) {
  return (
    <HStack
      justify="flex-end"
      spacing={4}
      flexShrink={0}
      onClick={(event: MouseEvent) => event.stopPropagation()}
    >
      <DraftEditCampaignButton campaignId={campaignId} isDisabled={isDeleting} />
      <IconButton
        aria-label="Delete draft campaign"
        icon={<FiTrash2 />}
        size="sm"
        variant="outline"
        bg="white"
        borderColor="gray.300"
        borderRadius="8px"
        color="red.500"
        boxShadow="sm"
        p={3}
        h="auto"
        minW="auto"
        onClick={onDelete}
        isLoading={isDeleting}
      />
    </HStack>
  );
}

export default function DraftCampaignRow({
  row,
  eventDateLabel,
  onDelete,
  isDeleting = false,
}: {
  row: AlgoliaAdCampaignObject;
  eventDateLabel?: string;
  onDelete: () => void;
  isDeleting?: boolean;
}) {
  const budget = formatDraftBudget(row.display_budget);
  const budgetUnset = budget === "Not set";
  const lastEdited = formatDraftLastEdited(row.updated_at);
  const navigate = useNavigate();
  const campaignPath = `/campaigns/ads/${row.objectID}`;
  const openCampaignDetails = () => navigate(campaignPath);

  return (
    <>
      <Grid
        display={{ base: "none", md: "grid" }}
        templateColumns={DRAFT_ROW_GRID_TEMPLATE}
        alignItems="center"
        columnGap={12}
        px={3}
        py={3}
        bg="gray.50"
        minW={DRAFT_TABLE_MIN_WIDTH}
        cursor="pointer"
        onClick={openCampaignDetails}
        _hover={{ bg: "gray.100" }}
        role="link"
        aria-label={`View ${row.name}`}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openCampaignDetails();
          }
        }}
      >
        <HStack minW={0} spacing={3} align="center">
          <Box
            width="60px"
            height="60px"
            borderRadius="sm"
            borderWidth="1px"
            borderColor="gray.300"
            overflow="hidden"
            flexShrink={0}
            bg="white"
          >
            {row.image_url ? (
              <Image
                src={row.image_url}
                alt={row.name}
                width="60px"
                height="60px"
                objectFit="cover"
              />
            ) : null}
          </Box>
          <VStack align="flex-start" spacing={1.5} minW={0} flex={1}>
            <Text
              fontSize="md"
              fontWeight="600"
              color="gray.800"
              lineHeight="1.2"
              noOfLines={2}
            >
              {row.name}
            </Text>
            {eventDateLabel ? (
              <Text fontSize="sm" color="gray.800" lineHeight="1">
                {eventDateLabel}
              </Text>
            ) : null}
          </VStack>
        </HStack>

        <HStack spacing={DRAFT_METRICS_GAP} minW={0}>
          <DraftMetricCell label="Last edited">{lastEdited}</DraftMetricCell>
          <DraftMetricCell label="Budget" muted={budgetUnset}>
            {budget}
          </DraftMetricCell>
        </HStack>

        <DraftCampaignRowActions
          campaignId={row.objectID}
          isDeleting={isDeleting}
          onDelete={onDelete}
        />
      </Grid>

      <VStack
        display={{ base: "flex", md: "none" }}
        align="stretch"
        spacing={4}
        px={3}
        py={3}
        bg="gray.50"
        cursor="pointer"
        onClick={openCampaignDetails}
        _hover={{ bg: "gray.100" }}
        role="link"
        aria-label={`View ${row.name}`}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openCampaignDetails();
          }
        }}
      >
        <HStack minW={0} spacing={3} align="center">
          <Box
            width="60px"
            height="60px"
            borderRadius="sm"
            borderWidth="1px"
            borderColor="gray.300"
            overflow="hidden"
            flexShrink={0}
            bg="white"
          >
            {row.image_url ? (
              <Image
                src={row.image_url}
                alt={row.name}
                width="60px"
                height="60px"
                objectFit="cover"
              />
            ) : null}
          </Box>
          <VStack align="flex-start" spacing={1.5} minW={0} flex={1}>
            <Text
              fontSize="md"
              fontWeight="600"
              color="gray.800"
              lineHeight="1.2"
              noOfLines={2}
            >
              {row.name}
            </Text>
            {eventDateLabel ? (
              <Text fontSize="sm" color="gray.800" lineHeight="1">
                {eventDateLabel}
              </Text>
            ) : null}
          </VStack>
        </HStack>

        <HStack spacing={8} pl="72px">
          <DraftMetricCell label="Last edited">{lastEdited}</DraftMetricCell>
          <DraftMetricCell label="Budget" muted={budgetUnset}>
            {budget}
          </DraftMetricCell>
        </HStack>

        <Box pl="72px" alignSelf="stretch">
          <DraftCampaignRowActions
            campaignId={row.objectID}
            isDeleting={isDeleting}
            onDelete={onDelete}
          />
        </Box>
      </VStack>
    </>
  );
}
